// components/member/CompetencyOverview.tsx
import { BadgeCheck } from "lucide-react";
import { convertToUserTime } from "../../utils/dateUtils/convertToUserTime";
import { getCompetencyStatus } from "../../utils/competencyUtils";
import { MemberCompetencyDTO } from "../../services/apiClient";

export default function CompetencyOverview({ items, showExpiringSoon = true }: { items: MemberCompetencyDTO[], showExpiringSoon?: boolean }) {

    const counts = { expired: 0, expiring: 0, active: 0 };
    const soon: MemberCompetencyDTO[] = [];

    for (const m of items) {
        const status = getCompetencyStatus(m); // already available in your code:contentReference[oaicite:0]{index=0}
        if (status === "expired") counts.expired++;
        else if (status === "expiring") counts.expiring++;
        else counts.active++;

        if (status === "expired" || status === "expiring") soon.push(m);
    }

    soon.sort(
        (a, b) =>
            new Date(a.competencyExpiryDate ?? 0).getTime() -
            new Date(b.competencyExpiryDate ?? 0).getTime()
    );

    return (
        <div className="space-y-6">
            {/* Status grid */}
            <div className="rounded-2xl bg-white p-4 shadow">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">COMPETENCY STATUS</h3>
                    <span className="rounded-full border px-2 py-0.5 text-xs">
                        Attention
                    </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <StatBox label="EXPIRED" value={counts.expired} color="text-red-600" />
                    <StatBox label="EXPIRING" value={counts.expiring} color="text-orange-600" />
                    <StatBox label="ACTIVE" value={counts.active} color="text-green-600" />
                </div>
            </div>

            {/* Expired / Expiring Soon list */}
            {showExpiringSoon && (
                <div className="rounded-2xl bg-white p-4 shadow hidden md:block">
                    <h3 className="mb-3 text-sm font-semibold">EXPIRED / EXPIRING SOON</h3>
                    <ul className="space-y-3">
                        {soon.slice(0, 5).map((m) => (
                            <li key={m.id} className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    {m.competencyCertificateId && (
                                        <div className="h-10 w-10 flex items-center justify-center rounded bg-gray-100">
                                            <BadgeCheck className="w-6 h-6 text-gray-600" />
                                        </div>)}
                                    <div>
                                        <div className="text-sm font-semibold">{m.competencyType?.title}</div>

                                        <div className={`text-xs ${getCompetencyStatus(m) === "expired" ? "text-red-600" : "text-yellow-600"}`}>
                                            {getCompetencyStatus(m) === "expired" ? "Expired" : "Expiring"}:{" "}
                                            {m.competencyExpiryDate
                                                ? convertToUserTime(String(m.competencyExpiryDate), true)
                                                : "n/a"}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                        {soon.length === 0 && (
                            <li className="text-xs text-gray-500">Nothing to show</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div className="rounded-lg bg-gray-50 p-4 text-center">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-600">{label}</div>
        </div>
    );
}
